import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

export interface GifData {
  id: string;
  url: string;
  previewUrl: string;
  width: number;
  height: number;
  title: string;
}

interface GifPickerProps {
  onGifSelect: (gif: GifData) => void;
  onClose: () => void;
}

interface GifSearchResponse {
  success: boolean;
  data?: {
    gifs: GifData[];
    nextCursor?: string;
    query: string;
    limit: number;
  };
  message?: string;
}

const GifPicker: React.FC<GifPickerProps> = ({ onGifSelect, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [gifs, setGifs] = useState<GifData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTrending, setShowTrending] = useState(true);
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get access token from auth store
  const { accessToken } = useAuthStore();

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Load trending GIFs on mount
  useEffect(() => {
    loadTrendingGifs();
    // Focus search input after component mounts
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      if (!showTrending) {
        loadTrendingGifs();
      }
      return;
    }

    setShowTrending(false);
    const timeoutId = setTimeout(() => {
      searchGifs(searchQuery.trim());
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const loadTrendingGifs = async () => {
    setLoading(true);
    setError(null);
    setShowTrending(true);


    try {
      const response = await fetch('/api/gifs/trending?limit=20', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data: GifSearchResponse = await response.json();

      if (data.success && data.data) {
        setGifs(data.data.gifs);
      } else {
        setError(data.message || 'Failed to load trending GIFs');
        setGifs([]);
      }
    } catch (err) {
      console.error('Failed to load trending GIFs:', err);
      setError('Kon trending GIFs niet laden');
      setGifs([]);
    } finally {
      setLoading(false);
    }
  };

  const searchGifs = async (query: string) => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/gifs/search?q=${encodeURIComponent(query)}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data: GifSearchResponse = await response.json();

      if (data.success && data.data) {
        setGifs(data.data.gifs);
      } else {
        setError(data.message || 'Search failed');
        setGifs([]);
      }
    } catch (err) {
      console.error('GIF search failed:', err);
      setError('Zoeken naar GIFs mislukt');
      setGifs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGifClick = (gif: GifData) => {
    onGifSelect(gif);
    onClose(); // Close picker after selection
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowTrending(true);
    loadTrendingGifs();
  };

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full left-0 mb-2 w-80 bg-surface rounded-card border border-white/[.08] shadow-panel overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/[.06]">
        <h3 className="text-sm font-semibold text-fg-bright">
          {showTrending ? 'Trending GIFs' : 'Zoek GIFs'}
        </h3>
        <button
          onClick={onClose}
          className="text-fg-faint hover:text-fg-bright transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Search input */}
      <div className="p-3 border-b border-white/[.06]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-fg-faint" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Zoek naar GIFs..."
            className="sf-input pl-10 pr-10 py-2 text-sm"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-fg-faint hover:text-fg-bright transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-2 text-fg-muted">
              <Loader2 className="h-5 w-5 animate-spin text-neon-cyan" />
              <span className="text-sm">
                {showTrending ? 'Trending GIFs laden...' : 'Zoeken...'}
              </span>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <p className="text-sm text-neon-pink-soft mb-2">{error}</p>
            <button
              onClick={showTrending ? loadTrendingGifs : () => searchGifs(searchQuery)}
              className="text-xs font-semibold text-neon-cyan hover:opacity-80 transition-opacity"
            >
              Probeer opnieuw
            </button>
          </div>
        ) : gifs.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-fg-muted text-center italic">
              {searchQuery ? 'Geen GIFs gevonden' : 'Geen trending GIFs beschikbaar'}
            </p>
          </div>
        ) : (
          <div className="p-3">
            <div className="grid grid-cols-2 gap-2">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => handleGifClick(gif)}
                  className="relative aspect-square bg-white/[.05] rounded-lg overflow-hidden hover:ring-2 hover:ring-neon-pink/60 transition-all group"
                  title={gif.title}
                >
                  <img
                    src={gif.previewUrl}
                    alt={gif.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 bg-white/[.03] border-t border-white/[.06]">
        <p className="text-xs text-fg-faint text-center">
          💎 Premium feature - {gifs.length} GIFs beschikbaar
        </p>
      </div>
    </div>
  );
};

export default GifPicker;